import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Minus, ShoppingBag, ArrowUpRight, Check, ChevronLeft, Heart, Sparkles, ChefHat, Award, Leaf, ShieldCheck, Globe, FlaskConical, Package } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import TickerBar from "@/components/TickerBar";
import { PRODUCTS, PACK_SIZES } from "@/data/products";
import { useCart } from "@/context/CartContext";

const certs = [
    { icon: ShieldCheck, label: "FSSAI Licensed" },
    { icon: Award, label: "ISO 22000" },
    { icon: Leaf, label: "100% Natural" },
    { icon: Globe, label: "Export ready" },
    { icon: FlaskConical, label: "Lab tested" },
    { icon: Sparkles, label: "No additives" },
];

// Branded info panels that replace the old lifestyle photos in slots 2 & 3
const BenefitsPanel = ({ product }) => (
    <div className="w-full h-full bg-sunrise text-white p-6 md:p-10 flex flex-col justify-between relative overflow-y-auto">
        <div className="absolute -top-20 -right-20 w-64 h-64 sun-rays opacity-25 animate-spin-slow pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-white/10 blur-3xl pointer-events-none" />

        <div className="relative">
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1.5 rounded-full mb-6">
                <Heart className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-medium">
                    Health benefits
                </span>
            </div>
            <h3 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em] mb-2">
                Why your body
            </h3>
            <h3 className="font-heading display-italic text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em]">
                will thank you.
            </h3>
        </div>

        <ul className="relative space-y-3 md:space-y-4 mt-8">
            {product.benefits.map((b, i) => (
                <li key={b} className="flex gap-3 items-start">
                    <div className="w-7 h-7 rounded-full bg-white text-sun-orange flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                        <span className="font-mono text-[10px] font-bold">0{i + 1}</span>
                    </div>
                    <span className="text-sm md:text-base leading-snug font-medium">{b}</span>
                </li>
            ))}
        </ul>

        <div className="relative mt-8 pt-6 border-t border-white/25 flex items-center justify-between gap-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] opacity-80">
                ostrich foods · {product.name.toLowerCase()}
            </div>
            <div className="bg-white/15 backdrop-blur px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]">
                100% pure
            </div>
        </div>
    </div>
);

const FSSAI_LICENSE = "10725997001012";

// Backside-of-pouch info panel: license, ingredients, nutrition, batch, QR
const BackPanel = ({ product }) => {
    const today = new Date();
    const mfg = `${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
    const expDate = new Date(today.getFullYear() + 2, today.getMonth());
    const exp = `${String(expDate.getMonth() + 1).padStart(2, "0")}/${expDate.getFullYear()}`;
    const batch = `OF${product.id.slice(0, 3).toUpperCase()}${today.getFullYear().toString().slice(-2)}`;

    return (
        <div className="w-full h-full bg-sun-cream text-sun-ink p-5 md:p-8 flex flex-col relative overflow-y-auto">
            {/* Top brand bar */}
            <div className="flex items-center justify-between pb-3 border-b border-sun-ink/15">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-white border border-sun-line p-1 flex items-center justify-center">
                        <img src="/logo.jpg" alt="Ostrich" className="w-full h-full object-contain" />
                    </div>
                    <div>
                        <div className="font-heading text-lg leading-none text-sun-ink">Ostrich Foods</div>
                        <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted mt-0.5">
                            Make your health better
                        </div>
                    </div>
                </div>
                <div className="text-right">
                    <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted">Net Wt.</div>
                    <div className="font-heading text-base leading-none">250 g</div>
                </div>
            </div>

            {/* Product name strip */}
            <div className="mt-3 mb-4">
                <div className="font-heading text-2xl md:text-3xl leading-tight tracking-[-0.02em]">
                    {product.name}
                </div>
                <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-sun-orange mt-1">
                    100% Natural · No additives
                </div>
            </div>

            {/* Ingredients & Nutrition */}
            <div className="grid grid-cols-2 gap-3">
                <div className="bg-white border border-sun-line rounded-xl p-3">
                    <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted mb-1.5">
                        Ingredients
                    </div>
                    <div className="text-[11px] md:text-xs leading-snug text-sun-ink">
                        {product.ingredients || `100% Pure ${product.name} (dehydrated, milled).`}
                    </div>
                </div>
                <div className="bg-white border border-sun-line rounded-xl p-3">
                    <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted mb-1.5">
                        Nutrition · per 10g
                    </div>
                    <div className="space-y-1 text-[10px] md:text-[11px] font-mono">
                        {(product.nutrition || [
                            ["Energy", "34 kcal"],
                            ["Protein", "1.2 g"],
                            ["Carbs", "7.5 g"],
                            ["Fat", "0.2 g"],
                        ]).map(([k, v]) => (
                            <div key={k} className="flex justify-between">
                                <span className="text-sun-inkSoft">{k}</span>
                                <span className="font-semibold">{v}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Storage strip */}
            <div className="mt-3 bg-white border border-sun-line rounded-xl p-3">
                <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted mb-1">Storage</div>
                <div className="text-[10px] md:text-[11px] leading-snug text-sun-ink">
                    Store in a cool, dry place away from direct sunlight. Keep tightly sealed after opening.
                </div>
            </div>

            {/* License & QR row */}
            <div className="mt-3 grid grid-cols-3 gap-3 flex-1">
                <div className="col-span-2 bg-white border border-sun-line rounded-xl p-3 flex flex-col justify-between">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <div className="px-2 py-0.5 rounded bg-sun-ink text-white font-mono text-[9px] tracking-[0.18em] font-bold">
                                FSSAI
                            </div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted">
                                License Number
                            </div>
                        </div>
                        <div className="font-mono text-base md:text-lg font-bold tracking-wider text-sun-ink">
                            {FSSAI_LICENSE}
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-sun-ink/10">
                        <div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-sun-muted">MFG</div>
                            <div className="font-mono text-[10px] font-semibold">{mfg}</div>
                        </div>
                        <div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-sun-muted">EXP</div>
                            <div className="font-mono text-[10px] font-semibold">{exp}</div>
                        </div>
                        <div>
                            <div className="font-mono text-[8px] uppercase tracking-[0.18em] text-sun-muted">Batch</div>
                            <div className="font-mono text-[10px] font-semibold">{batch}</div>
                        </div>
                    </div>
                </div>

                {/* QR code (CSS-pattern placeholder) */}
                <div className="bg-white border border-sun-line rounded-xl p-2 flex flex-col items-center justify-center">
                    <div className="w-full aspect-square bg-sun-ink rounded-md p-1.5">
                        <div
                            className="w-full h-full"
                            style={{
                                backgroundImage:
                                    "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
                                backgroundSize: "5px 5px",
                            }}
                        />
                    </div>
                    <div className="font-mono text-[7px] uppercase tracking-[0.16em] text-sun-muted mt-1 text-center">
                        Scan to verify
                    </div>
                </div>
            </div>

            {/* Footer address */}
            <div className="mt-3 pt-3 border-t border-sun-ink/15 flex items-start justify-between gap-3">
                <div>
                    <div className="font-mono text-[8px] uppercase tracking-[0.22em] text-sun-muted">
                        Manufactured & marketed by
                    </div>
                    <div className="font-mono text-[10px] text-sun-ink leading-tight mt-0.5">
                        Ostrich Foods · Surat, Gujarat, India
                    </div>
                </div>
                <a href="mailto:trade@ostrichfoods.in" className="font-mono text-[10px] text-sun-orange whitespace-nowrap">
                    ostrichfoods.in
                </a>
            </div>
        </div>
    );
};

const HowToPanel = ({ product }) => (
    <div className="w-full h-full bg-white p-6 md:p-10 flex flex-col justify-between relative overflow-y-auto">
        <div className="absolute -bottom-32 -right-20 w-72 h-72 rounded-full bg-sunrise opacity-15 blur-3xl pointer-events-none" />

        <div className="relative">
            <div className="inline-flex items-center gap-2 bg-sun-cream px-3 py-1.5 rounded-full mb-6">
                <ChefHat className="w-3.5 h-3.5 text-sun-orange" strokeWidth={2.5} />
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] font-medium text-sun-ink">
                    How to use it
                </span>
            </div>
            <h3 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em] text-sun-ink">
                One spoon,
            </h3>
            <h3 className="font-heading display-italic text-gradient text-3xl md:text-5xl leading-[0.95] tracking-[-0.02em]">
                infinite ways.
            </h3>
            <p className="mt-5 md:mt-6 text-sun-inkSoft text-sm md:text-base leading-relaxed">
                {product.usage}
            </p>
        </div>

        <div className="relative mt-8 space-y-5">
            <div>
                <div className="overline mb-3">— Best used in</div>
                <div className="flex flex-wrap gap-2">
                    {product.applications.map((a) => (
                        <span
                            key={a}
                            className="px-3 py-1.5 rounded-full bg-sun-cream font-mono text-[10px] uppercase tracking-[0.18em] text-sun-ink"
                        >
                            {a}
                        </span>
                    ))}
                </div>
            </div>

            <div className="border-t border-sun-line pt-5">
                <div className="overline mb-3">— Crafted with</div>
                <div className="grid grid-cols-3 gap-2">
                    {[
                        { icon: Leaf, label: "Natural" },
                        { icon: ShieldCheck, label: "FSSAI" },
                        { icon: Sparkles, label: "No additives" },
                    ].map((b) => (
                        <div
                            key={b.label}
                            className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-sun-cream"
                        >
                            <b.icon className="w-4 h-4 text-sun-orange" strokeWidth={2} />
                            <span className="font-mono text-[9px] uppercase tracking-[0.18em] text-sun-ink text-center leading-tight">
                                {b.label}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="text-center pt-2">
                <div className="font-heading display-italic text-2xl text-sun-ink">36 hours</div>
                <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-sun-muted mt-1">
                    farm to sealed pouch
                </div>
            </div>
        </div>
    </div>
);

const ProductDetail = () => {
    const { id } = useParams();
    const product = PRODUCTS.find((p) => p.id === id);
    const [size, setSize] = useState("250g");
    const [qty, setQty] = useState(1);
    const [activeSlide, setActiveSlide] = useState(0);
    const { addItem, setOpen } = useCart();

    // Reset scroll + active slide when navigating between products
    useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
        setActiveSlide(0);
    }, [id]);

    const related = useMemo(
        () => (product ? PRODUCTS.filter((p) => p.category === product.category && p.id !== product.id).slice(0, 4) : []),
        [product],
    );

    if (!product) {
        return (
            <>
                <Navbar />
                <main className="container-x pt-40 pb-32 text-center min-h-screen">
                    <h1 className="font-heading text-5xl md:text-7xl text-sun-ink">Not found.</h1>
                    <p className="mt-4 text-sun-inkSoft">We couldn't find that product.</p>
                    <Link to="/" className="mt-8 inline-block btn-gradient">
                        Back to catalogue
                    </Link>
                </main>
                <Footer />
            </>
        );
    }

    // 4 slides: branded pouch · health benefits · how-to-use · backside (license/nutrition)
    const slides = [
        { type: "image", thumbLabel: "Pouch" },
        { type: "benefits", thumbLabel: "Benefits" },
        { type: "howto", thumbLabel: "How to use" },
        { type: "back", thumbLabel: "Back of pack" },
    ];

    const handleAdd = () => {
        addItem(product, size, qty);
        toast.success(`${product.name} · ${size} added`, {
            description: `Quantity: ${qty}`,
            action: { label: "View cart", onClick: () => setOpen(true) },
        });
    };

    const renderSlide = (i) => {
        const s = slides[i];
        if (s.type === "image") {
            return (
                <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                    data-testid="product-detail-main-image"
                />
            );
        }
        if (s.type === "benefits") return <BenefitsPanel product={product} />;
        if (s.type === "back") return <BackPanel product={product} />;
        return <HowToPanel product={product} />;
    };

    return (
        <>
            <Navbar />
            <main className="bg-sun-bg pt-28 md:pt-32 pb-24" data-testid="product-detail">
                <div className="container-x">
                    <nav className="flex items-center gap-2 mb-8 font-mono text-[10px] uppercase tracking-[0.28em] text-sun-muted">
                        <Link to="/" className="hover:text-sun-orange transition-colors flex items-center gap-1">
                            <ChevronLeft className="w-3 h-3" /> Home
                        </Link>
                        <span>/</span>
                        <Link to="/#products" className="hover:text-sun-orange transition-colors">
                            Catalogue
                        </Link>
                        <span>/</span>
                        <span className="text-sun-ink">{product.name}</span>
                    </nav>

                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
                        {/* Gallery */}
                        <div className="lg:col-span-7">
                            <div className="min-h-[420px] md:min-h-[520px] lg:aspect-square lg:min-h-0 bg-gradient-to-br from-white to-sun-cream rounded-3xl overflow-hidden border border-sun-line">
                                {renderSlide(activeSlide)}
                            </div>
                            <div className="mt-4 grid grid-cols-4 gap-3">
                                {slides.map((s, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveSlide(i)}
                                        data-testid={`product-detail-thumb-${i}`}
                                        className={`aspect-square rounded-2xl overflow-hidden border-2 transition-all ${
                                            activeSlide === i
                                                ? "border-sun-orange shadow-md"
                                                : "border-sun-line hover:border-sun-ink/40"
                                        }`}
                                    >
                                        <div className="w-full h-full relative">
                                            {s.type === "image" && (
                                                <div className="w-full h-full bg-gradient-to-br from-white to-sun-cream">
                                                    <img src={product.image} alt="" className="w-full h-full object-contain p-1" />
                                                </div>
                                            )}
                                            {s.type === "benefits" && (
                                                <div className="w-full h-full bg-sunrise text-white flex flex-col items-center justify-center p-2">
                                                    <Heart className="w-5 h-5 md:w-6 md:h-6 mb-1" strokeWidth={2} />
                                                    <span className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.16em] font-medium text-center">
                                                        Health<br />benefits
                                                    </span>
                                                </div>
                                            )}
                                            {s.type === "howto" && (
                                                <div className="w-full h-full bg-sun-ink text-sun-bg flex flex-col items-center justify-center p-2">
                                                    <ChefHat className="w-5 h-5 md:w-6 md:h-6 mb-1 text-sun-gold" strokeWidth={2} />
                                                    <span className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.16em] font-medium text-center">
                                                        How to<br />use it
                                                    </span>
                                                </div>
                                            )}
                                            {s.type === "back" && (
                                                <div className="w-full h-full bg-sun-cream flex flex-col items-center justify-center p-2 border border-sun-line">
                                                    <Package className="w-5 h-5 md:w-6 md:h-6 mb-1 text-sun-ink" strokeWidth={2} />
                                                    <span className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.16em] font-medium text-center text-sun-ink">
                                                        Back of<br />pack
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Buy box */}
                        <div className="lg:col-span-5">
                            <div className="overline mb-3">— {product.category}</div>
                            <h1 className="font-heading text-5xl md:text-6xl leading-[0.95] tracking-[-0.02em] text-sun-ink">
                                {(() => {
                                    const suffixes = ["Powder", "Spice", "Flakes"];
                                    for (const s of suffixes) {
                                        if (product.name.endsWith(` ${s}`)) {
                                            return (
                                                <>
                                                    {product.name.slice(0, -s.length - 1)}
                                                    <span className="display-italic text-gradient">{` ${s.toLowerCase()}.`}</span>
                                                </>
                                            );
                                        }
                                    }
                                    const tail = { Nut: " kernels.", Beverage: " blend.", Wellness: " root." }[product.category] || " · pure.";
                                    return (
                                        <>
                                            {product.name}
                                            <span className="display-italic text-gradient">{tail}</span>
                                        </>
                                    );
                                })()}
                            </h1>
                            <p className="mt-4 text-lg text-sun-inkSoft leading-relaxed">
                                {product.tagline}
                            </p>

                            <div className="mt-8">
                                <div className="overline mb-2">— Pack size</div>
                                <div className="grid grid-cols-3 gap-1 p-1 bg-sun-cream rounded-full max-w-md">
                                    {PACK_SIZES.map((s) => (
                                        <button
                                            key={s}
                                            onClick={() => setSize(s)}
                                            data-testid={`detail-pack-${s}`}
                                            className={`py-3 font-mono text-xs uppercase tracking-[0.18em] rounded-full transition-colors ${
                                                size === s
                                                    ? "bg-sun-ink text-sun-bg"
                                                    : "text-sun-inkSoft hover:text-sun-ink"
                                            }`}
                                        >
                                            {s}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="mt-6 flex items-end justify-between border-y border-sun-line py-6">
                                <div>
                                    <div className="overline">— Price</div>
                                    <div data-testid="detail-price" className="font-heading text-5xl mt-1 text-sun-ink">
                                        ₹{product.prices[size]}
                                    </div>
                                </div>
                                <div className="flex items-center bg-sun-cream rounded-full">
                                    <button
                                        onClick={() => setQty((q) => Math.max(1, q - 1))}
                                        className="w-10 h-10 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <div className="w-9 text-center font-mono text-sm font-semibold">{qty}</div>
                                    <button
                                        onClick={() => setQty((q) => q + 1)}
                                        className="w-10 h-10 flex items-center justify-center text-sun-inkSoft hover:text-sun-orange rounded-full"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            <button
                                onClick={handleAdd}
                                data-testid="detail-add-to-cart"
                                className="btn-gradient w-full mt-6 py-5 text-base"
                            >
                                Add to cart
                                <ShoppingBag className="w-5 h-5" />
                            </button>

                            <div className="mt-4 bg-sun-cream rounded-2xl p-4 flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-sun-ink text-sun-gold flex items-center justify-center font-mono text-xs font-bold flex-shrink-0">
                                    %
                                </div>
                                <div className="text-sm leading-tight">
                                    <span className="font-medium text-sun-ink">10% off</span>
                                    <span className="text-sun-inkSoft"> auto-applied at checkout with code </span>
                                    <span className="font-mono font-semibold text-sun-orange">AVAIL10</span>
                                </div>
                            </div>

                            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-sun-inkSoft">
                                <span className="px-2 py-0.5 rounded bg-sun-ink text-white tracking-[0.16em] font-bold">FSSAI</span>
                                <span>Lic. No.</span>
                                <span className="font-semibold tracking-wider text-sun-ink">{FSSAI_LICENSE}</span>
                            </div>

                            <div className="mt-8 grid grid-cols-2 gap-3">
                                {[
                                    "100% Pure",
                                    "100% Natural",
                                    "No Added Colour",
                                    "No Preservative",
                                    "Gluten Free",
                                    "No GMO",
                                    "No Added Sugar",
                                    "No Added Flavour",
                                ].map((b) => (
                                    <div key={b} className="flex items-center gap-2 text-sm">
                                        <Check className="w-4 h-4 text-sun-orange flex-shrink-0" strokeWidth={2.5} />
                                        <span className="text-sun-ink">{b}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Description block */}
                    <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-10">
                        <div className="md:col-span-7">
                            <div className="overline mb-4">— About this powder</div>
                            <h2 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-[-0.02em] text-sun-ink mb-6">
                                Story in a <span className="display-italic text-gradient">spoonful.</span>
                            </h2>
                            <p className="text-lg text-sun-inkSoft leading-relaxed">{product.description}</p>

                            <div className="mt-10 bg-white border border-sun-line rounded-3xl p-8">
                                <div className="overline mb-3">— Suggested use</div>
                                <p className="text-sun-ink leading-relaxed">{product.usage}</p>
                            </div>
                        </div>

                        <div className="md:col-span-5">
                            <div className="bg-sun-ink text-sun-bg rounded-3xl p-8 md:p-10">
                                <div className="overline text-sun-goldSoft mb-4">— Why it's good for you</div>
                                <ul className="space-y-4">
                                    {product.benefits.map((b) => (
                                        <li key={b} className="flex gap-3 items-start">
                                            <div className="w-6 h-6 rounded-full bg-sunrise text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <Check className="w-3 h-3" strokeWidth={2.5} />
                                            </div>
                                            <span className="text-base leading-snug">{b}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="mt-4 bg-white border border-sun-line rounded-3xl p-6">
                                <div className="overline mb-4">— Best used in</div>
                                <div className="flex flex-wrap gap-2">
                                    {product.applications.map((a) => (
                                        <span
                                            key={a}
                                            className="px-3 py-1.5 rounded-full bg-sun-cream font-mono text-[11px] uppercase tracking-[0.18em] text-sun-ink"
                                        >
                                            {a}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Certifications */}
                    <div className="mt-20">
                        <div className="overline mb-4">— Certifications & assurance</div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            {certs.map((c) => (
                                <div
                                    key={c.label}
                                    className="bg-white border border-sun-line rounded-2xl p-5 flex flex-col items-center gap-3 text-center hover:border-sun-orange transition-colors"
                                >
                                    <div className="w-12 h-12 rounded-full bg-sunrise flex items-center justify-center text-white">
                                        <c.icon className="w-5 h-5" strokeWidth={2} />
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sun-ink">
                                        {c.label}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Related */}
                    {related.length > 0 && (
                        <div className="mt-24">
                            <div className="overline mb-4">— More from {product.category}</div>
                            <h3 className="font-heading text-4xl md:text-5xl leading-[0.95] tracking-tight text-sun-ink mb-8">
                                You might also <span className="display-italic text-gradient">love.</span>
                            </h3>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                {related.map((r) => (
                                    <Link
                                        key={r.id}
                                        to={`/product/${r.id}`}
                                        data-testid={`related-${r.id}`}
                                        className="group bg-white border border-sun-line rounded-3xl overflow-hidden hover:shadow-xl hover:shadow-sun-orange/10 transition-all"
                                    >
                                        <div className="aspect-square bg-gradient-to-br from-white to-sun-cream">
                                            <img
                                                src={r.image}
                                                alt={r.name}
                                                className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-700"
                                            />
                                        </div>
                                        <div className="p-4">
                                            <div className="font-heading text-xl text-sun-ink leading-tight">
                                                {r.name}
                                            </div>
                                            <div className="mt-1 font-mono text-[11px] text-sun-orange">
                                                from ₹{r.prices["250g"]}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Big CTA */}
                    <div className="mt-24 bg-sunrise text-white rounded-3xl p-10 md:p-16 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-[0.28em] opacity-80">
                                — Trade desk
                            </div>
                            <h3 className="font-heading text-3xl md:text-5xl leading-tight mt-2 max-w-xl">
                                Need {product.name.toLowerCase()}{" "}
                                <span className="display-italic">by the tonne?</span>
                            </h3>
                        </div>
                        <Link to="/#bulk" className="bg-white text-sun-ink px-7 py-4 rounded-full font-medium hover:bg-sun-ink hover:text-white transition-colors inline-flex items-center gap-2">
                            Request a quote <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </div>
            </main>
            <TickerBar />
            <Footer />
            <CartDrawer />
            <WhatsAppFloat />
        </>
    );
};

export default ProductDetail;
